import { useState } from 'react';

import { isDesktop } from '../../app/desktop';
import { apiUrl } from '../../lib/api';
import { beginHandoff } from './desktopHandoff';
import { useAuthProviders } from './providers';

/**
 * The Discord logo. Inlined rather than fetched, because a sign-in button that
 * draws its icon a beat after the page is a sign-in button people click twice.
 */
function DiscordMark() {
  return (
    <svg viewBox="0 0 127 96" aria-hidden="true" className="h-4 w-4 shrink-0" fill="currentColor">
      <path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.15ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69Z" />
    </svg>
  );
}

const STYLE = [
  'inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius-pill)]',
  'border border-line-strong px-5 py-2.5 text-sm font-medium text-ink',
  'transition-colors hover:border-line-lit hover:bg-raised disabled:cursor-not-allowed disabled:opacity-50',
].join(' ');

/**
 * "Continue with Discord", above the email form.
 *
 * ## In a browser it is an anchor, and that is deliberate
 *
 * It points at a server route that redirects, so it can be an ordinary link.
 * That means it works before the bundle has finished hydrating, survives a
 * middle-click, and cannot fail in a way that needs its own error state - which
 * matters more here than almost anywhere else, because this is the first thing
 * a stranger touches.
 *
 * ## In the desktop app it has to be a button
 *
 * The shell would happily follow that link in its own window, and the redirect
 * to Discord would land there too, which is precisely the address-bar-less
 * login this arrangement exists to avoid. So the app opens the system browser
 * instead, and takes a one-time code back afterwards - see `desktopHandoff`.
 *
 * `window.open` rather than a bridge call: the shell already sends every
 * `window.open` to the real browser, so this needs no new surface between the
 * page and the main process, and an older shell handles it identically.
 *
 * ## Nothing is drawn when nothing is configured
 *
 * No credentials on the server, no button, no divider, no gap. A development
 * machine shows the email form exactly as it did before this existed.
 */
export function ProviderSignIn({ next }: { next?: string }) {
  const { data } = useAuthProviders();
  const [opening, setOpening] = useState(false);

  if (data?.providers.discord !== true) return null;

  const query = next === undefined ? '' : `?next=${encodeURIComponent(next)}`;
  const href = apiUrl(`/auth/discord/start${query}`);

  async function openInBrowser() {
    setOpening(true);

    const challenge = await beginHandoff();

    if (challenge === '') {
      /*
       * No secure crypto, so no verifier, so no safe handoff. Refusing is the
       * right answer: the alternative is a code coming home through a URL with
       * nothing else protecting it.
       */
      setOpening(false);

      return;
    }

    const separator = query === '' ? '?' : '&';

    window.open(`${href}${separator}client=desktop&challenge=${challenge}`, '_blank');
    setOpening(false);
  }

  return (
    <div className="flex flex-col gap-4" data-provider-signin>
      {isDesktop ? (
        <button type="button" onClick={() => void openInBrowser()} disabled={opening} data-provider="discord" className={STYLE}>
          <DiscordMark />
          Continue with Discord
        </button>
      ) : (
        <a href={href} data-provider="discord" className={STYLE}>
          <DiscordMark />
          Continue with Discord
        </a>
      )}

      {isDesktop && (
        <p className="text-center text-[13px] leading-relaxed text-faint">
          This opens your browser, so you can see who you are signing in to. Come back here when it is done.
        </p>
      )}

      {/*
        A labelled rule rather than a bare one. Two stacked ways to sign in with
        nothing between them reads as a mistake in the layout.
      */}
      <div className="flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-line" />
        <span className="meta-caps text-ghost">or</span>
        <span className="h-px flex-1 bg-line" />
      </div>
    </div>
  );
}
